import {
  Box,
  Button,
  chakra,
  Field,
  Flex,
  Heading,
  Icon,
  Input,
  InputProps,
  Spinner,
  Stack,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import React from "react";
import { LuCheck, LuEye, LuEyeOff, LuX } from "react-icons/lu";
import type { PasswordMatch } from "./passwordRules";

/** Vertical rhythm of the login forms — the one gap between every element. */
export const FORM_GAP = 3;

/**
 * The visual building blocks of the login forms. The forms (magic link,
 * password) own all behaviour; an app can swap the look by passing its own
 * implementations of this interface to `<LoginPage ui={…} />`. The defaults
 * below are the original core look, so apps that pass nothing are unchanged.
 */
export interface LoginUI {
  /**
   * Text input. Receives the usual Chakra `Input` props (type, value, …).
   * For `type="password"` it must offer its own show/hide toggle, and honour
   * the extra props in `LoginInputProps`.
   */
  Input: React.ComponentType<LoginInputProps>;
  /** The form's submit button. */
  SubmitButton: React.ComponentType<{
    isLoading: boolean;
    disabled: boolean;
    children: React.ReactNode;
  }>;
  /** Quiet secondary action ("Login with password" / "…with magic-link"). */
  GhostButton: React.ComponentType<{
    onClick: () => void;
    children: React.ReactNode;
  }>;
}

/** What the forms pass to `LoginUI.Input`. The extras are for password inputs only. */
export interface LoginInputProps extends InputProps {
  /** Set to keep a slot for `PasswordMatchIcon` next to the eye. */
  match?: PasswordMatch;
  /** Pass both to share one eye between boxes; without them each box has its own. */
  isRevealed?: boolean;
  onToggleReveal?: () => void;
}

/** The eye's state: the page's when it passes one, else the input's own. */
export function useRevealState(
  isRevealed: boolean | undefined,
  onToggleReveal: (() => void) | undefined,
): [boolean, () => void] {
  const [isOwnRevealed, setIsOwnRevealed] = React.useState(false);
  if (isRevealed !== undefined && onToggleReveal) {
    return [isRevealed, onToggleReveal];
  }
  return [isOwnRevealed, () => setIsOwnRevealed((v) => !v)];
}

const MATCH_TEXT: Record<PasswordMatch, string> = {
  none: "",
  match: "Passwords match",
  mismatch: "Passwords don't match",
};

/** The repeat box's ✓ / ✗. Its slot is always there, so showing it moves nothing. */
export function PasswordMatchIcon({ match }: { match: PasswordMatch }) {
  // Two stacked icons, so a fade-out never shows the wrong glyph or colour.
  const glyph = {
    position: "absolute",
    inset: 0,
    boxSize: 4,
    transition: "opacity 0.15s",
    css: { strokeWidth: 3 },
    "aria-hidden": true,
  } as const;

  return (
    <Box position="relative" boxSize={4} flexShrink={0} pointerEvents="none">
      <Icon
        as={LuCheck}
        color="green.300"
        opacity={match === "match" ? 1 : 0}
        {...glyph}
      />
      <Icon
        as={LuX}
        color="red.400"
        opacity={match === "mismatch" ? 1 : 0}
        {...glyph}
      />
      {/* The icons are silent; screen readers get the words. */}
      <VisuallyHidden aria-live="polite">{MATCH_TEXT[match]}</VisuallyHidden>
    </Box>
  );
}

export const loginInputStyles = {
  size: "lg" as const,
  borderRadius: "full",
  borderWidth: "0px",
  _focus: {
    outlineColor: "brand.200",
    outlineWidth: "2px",
  },
  backgroundColor: "whiteAlpha.200",
  height: "45px",
  _placeholder: {
    color: "whiteAlpha.500",
    fontSize: "14px",
  },
};

export function LoginInput({
  match,
  isRevealed: revealedProp,
  onToggleReveal,
  ...props
}: LoginInputProps) {
  const [isRevealed, toggleReveal] = useRevealState(
    revealedProp,
    onToggleReveal,
  );

  if (props.type !== "password") {
    return <Input {...loginInputStyles} {...props} />;
  }

  return (
    <Box position="relative" width="100%">
      <Input
        {...loginInputStyles}
        {...props}
        type={isRevealed ? "text" : "password"}
        // Room for the toggle, and the match icon when there is one.
        paddingEnd={match ? "68px" : "48px"}
      />
      <Flex
        position="absolute"
        top="50%"
        insetEnd="12px"
        transform="translateY(-50%)"
        zIndex={1}
        alignItems="center"
        gap={1}
        // Clicks between the icons still reach the input.
        pointerEvents="none"
      >
        {match && <PasswordMatchIcon match={match} />}
        <chakra.button
          // Not "submit": this sits inside a form.
          type="button"
          aria-label={isRevealed ? "Hide password" : "Show password"}
          aria-pressed={isRevealed}
          onClick={toggleReveal}
          // Keep the caret in the field while toggling.
          onMouseDown={(e) => e.preventDefault()}
          pointerEvents="auto"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxSize="28px"
          borderRadius="full"
          border="none"
          backgroundColor="transparent"
          color="whiteAlpha.700"
          cursor="pointer"
          _hover={{ color: "white" }}
        >
          <Icon as={isRevealed ? LuEyeOff : LuEye} boxSize={4} />
        </chakra.button>
      </Flex>
    </Box>
  );
}

export function FormError({
  isValid,
  error,
}: {
  isValid: boolean;
  error: string | null;
}) {
  return (
    <>
      {!isValid && <Field.ErrorText>Invalid email address</Field.ErrorText>}
      {error && (
        <Text color="red.500" fontSize={"sm"}>
          {error}
        </Text>
      )}
    </>
  );
}

/**
 * A quiet notice card shown above the login field — a one-line title and a
 * short explanation. Used for "that link didn't work" and "magic link sent"
 * so both read as the same thing.
 */
export function LoginNotice({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack
      alignItems={"center"}
      gap={1}
      backgroundColor={"whiteAlpha.200"}
      p={4}
      borderRadius={"xl"}
    >
      <Heading fontSize={"md"} textAlign={"center"}>
        {title}
      </Heading>

      <Text textAlign={"center"} fontSize={"xs"} color={"whiteAlpha.600"}>
        {children}
      </Text>
    </Stack>
  );
}

export function SubmitButton({
  isLoading,
  disabled,
  children,
}: {
  isLoading: boolean;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="submit"
      width={"100%"}
      height={"45px"}
      backgroundColor="brand.200"
      disabled={disabled}
      borderRadius={"full"}
      color="brand.900"
      fontWeight={"semibold"}
      fontSize={"md"}
      _disabled={{ color: "brand.900" }}
    >
      {children}
      {isLoading && <Spinner color="white" size={"sm"} ml={2} />}
    </Button>
  );
}

export function GhostButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      mx="auto"
      height={"45px"}
      borderRadius={"full"}
      color="whiteAlpha.600"
      backgroundColor={"transparent"}
      fontSize={"sm"}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/** The core look — used when an app doesn't provide its own `LoginUI`. */
export const defaultLoginUI: LoginUI = {
  Input: LoginInput,
  SubmitButton,
  GhostButton,
};
